import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchNursingOPDAssessment } from "../../../store/slices/nursingOPDAssessmentsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function NursingOPDPrint() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentNursingOPDAssessment, isLoading, error } = useSelector((state) => state.nursingOPDAssessments);

  useEffect(() => {
    if (id) dispatch(fetchNursingOPDAssessment(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentNursingOPDAssessment) {
      const timer = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentNursingOPDAssessment, navigate]);

  if (isLoading) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <div className="p-8 text-danger text-center">{error}</div>;
  if (!currentNursingOPDAssessment) return <div className="p-8 text-center">No record found.</div>;

  const a = currentNursingOPDAssessment;
  const p = a.patient || {};
  const labs = a.labs || [];
  const plans = a.plans || [];

  const fmtDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "—";

  const renderCheck = (condition) => (
    <span style={{ fontSize: '14px', marginRight: '5px' }}>{condition ? "☑" : "☐"}</span>
  );

  return (
    <>
      <style jsx global>{`
        * {
          box-sizing: border-box !important;
          font-family: Arial, sans-serif !important;
        }

        body {
          background: #fff !important;
          color: #000 !important;
          font-size: 11px !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .print-container {
          padding: 30px !important;
          position: relative !important;
        }
        ${printFooterStyles}

        .logo {
          position: absolute !important;
          top: 15px !important;
          left: 15px !important;
        }

        .logo img {
          height: 60px !important;
        }

        .header {
          text-align: center !important;
          margin-bottom: 20px !important;
          border-bottom: 2px solid #000 !important;
          padding-bottom: 10px !important;
        }

        .header h3 {
          font-size: 1.5rem !important;
          font-weight: bold !important;
        }

        .header h6 {
          font-size: 1rem !important;
          text-decoration: underline !important;
        }

        .section-title {
          background: #eee !important;
          padding: 4px 8px !important;
          font-weight: bold !important;
          margin: 15px 0 8px 0 !important;
          border: 1px solid #000 !important;
          text-transform: uppercase !important;
        }

        .info-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-bottom: 10px !important;
        }

        .info-table td,
        .info-table th {
          border: 1px solid #000 !important;
          padding: 6px !important;
          vertical-align: top !important;
        }

        .label {
          font-weight: bold !important;
          width: 25% !important;
          background: #f9f9f9 !important;
        }

        .text-box {
          border: 1px solid #000 !important;
          padding: 8px !important;
          min-height: 50px !important;
          margin-top: 5px !important;
          white-space: pre-wrap !important;
        }

        .checkbox-grid {
          display: grid !important;
          grid-template-columns: repeat(3, 1fr) !important;
          gap: 5px !important;
          margin-bottom: 10px !important;
        }

        .checkbox-item {
          display: flex !important;
          align-items: center !important;
          margin-bottom: 4px !important;
        }

        .footer {
          margin-top: 30px !important;
          text-align: left !important;
        }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
          <div className="logo">
            <img src={logo} alt="TRH Logo" />
          </div>

          <div className="header">
            <h3>THE RECOVERY HOUSE</h3>
            <h6>Nursing OPD Assessment Report</h6>
          </div>

        {/* SECTION 1: IDENTIFICATION */}
        <div className="section-title">Client Identification &amp; Staff</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Client Name</td>
              <td>{p.first_name} {p.last_name}</td>
              <td className="label">CNIC</td>
              <td>{p.cnic || "—"}</td>
            </tr>
            <tr>
              <td className="label">Gender</td>
              <td>{p.gender || "—"}</td>
              <td className="label">DOB</td>
              <td>{p.dob || "—"}</td>
            </tr>
            <tr>
              <td className="label">Admitted To</td>
              <td>{a.admitted_to || "—"}</td>
              <td className="label">Consent Taken From</td>
              <td>{a.consent_taken_from || "—"}</td>
            </tr>
            <tr>
              <td className="label">Nursing Staff</td>
              <td>{a.nursing_name || "—"}</td>
              <td className="label">Nursing Supervisor</td>
              <td>{a.nursing_supervisor || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* SECTION 2: VITALS & PHYSICAL */}
        <div className="section-title">Vitals & Assessment</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Temp</td><td>{a.temperature || "—"} °F</td>
              <td className="label">Pulse</td><td>{a.pulse || "—"} bpm</td>
              <td className="label">BP</td><td>{a.blood_pressure || "—"} mmHg</td>
            </tr>
            <tr>
              <td className="label">RR</td><td>{a.respiratory_rate || "—"} bpm</td>
              <td className="label">Weight</td><td>{a.weight || "—"} kg</td>
              <td className="label">Height</td><td>{a.height || "—"} cm</td>
            </tr>
            <tr>
              <td className="label">BMI</td><td>{a.bmi || "—"}</td>
              <td className="label">RBS</td><td>{a.rbs || "—"} mg/dL</td>
              <td className="label">COVID Status</td><td>{a.covid_status || "—"}</td>
            </tr>
            <tr>
              <td className="label">ECG Done?</td>
              <td>{a.ecg_done}</td>
              <td className="label">ECG Reason (if No)</td>
              <td>{a.ecg_no_reason || "—"}</td>
            </tr>
            <tr>
              <td className="label">Mode of Transport</td>
              <td colSpan="3">{a.mode_of_transportation || "—"}</td>
            </tr>
          </tbody>
        </table>

        <table className="info-table">
          <tbody>
            <tr>
              <td className="label" style={{ width: '25%' }}>Physical Assessment</td>
              <td>{a.physical_assessment || "—"}</td>
            </tr>
            <tr>
              <td className="label">Skin Assessment</td>
              <td>{a.skin_assessment || "—"}</td>
            </tr>
            <tr>
              <td className="label">Medical Assessment</td>
              <td>{a.medical_assessment || "—"}</td>
            </tr>
          </tbody>
        </table>

        <div className="section-title">Ziauddin Referral Info</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Referral Ziauddin</td>
              <td>{a.referral_ziauddin || "—"}</td>
            </tr>
            <tr>
              <td className="label">Medical Condition</td>
              <td>{a.medical_condition || "—"}</td>
            </tr>
            <tr>
              <td className="label">Ziauddin Advise Brief</td>
              <td>{a.ziauddin_advise_brief || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* SECTION 3: RISKS */}
        <div className="section-title">Risk Assessment</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">ROH (Homicidal)</td><td>{a.roh_risk || "—"}</td>
              <td className="label">ROS (Suicide)</td><td>{a.ros_risk || "—"}</td>
            </tr>
            <tr>
              <td className="label">ROV (Violence)</td><td>{a.rov_risk || "—"}</td>
              <td className="label">ROE (Escape)</td><td>{a.roe_risk || "—"}</td>
            </tr>
            <tr>
              <td className="label">ROF (Falls)</td><td>{a.rof_risk || "—"}</td>
              <td className="label">ROVU (Vulnerability)</td><td>{a.rovu_risk || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* SECTION 4: RESTRAIN & INFO */}
        <div className="section-title">Restrain Patient & Information Given</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Order By</td><td>{a.restrain_order_by || "—"}</td>
              <td className="label">Type</td><td>{a.restrain_type || "—"}</td>
            </tr>
            <tr>
              <td className="label">Physical Restrain</td><td>{a.restrain_physical || "—"}</td>
              <td className="label">Chemical Restrain</td><td>{a.restrain_chemical || "—"}</td>
            </tr>
          </tbody>
        </table>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', padding: '5px' }}>
          {[
            { k: 'info_admission_process', l: 'Admission process and waiting time' },
            { k: 'info_inform_restrain', l: 'Inform about restrain' },
            { k: 'info_inform_ecg', l: 'Inform about ECG' },
            { k: 'info_inform_injection', l: 'Inform about any injection given' },
            { k: 'info_uniform_change', l: 'Changing of uniform in assessment room' },
            { k: 'info_introduce_staff', l: 'Introduce ward staff' },
            { k: 'info_belongings_not_allowed', l: 'Inform about patient belongings not allowed' },
            { k: 'info_provided_by_karwan', l: 'Everything needed provided by Karwan-e-Hayat' },
            { k: 'info_inform_food', l: 'Inform about food (private/welfare)' },
            { k: 'info_inform_canteen', l: 'Inform about canteen account' }
          ].map(item => (
            <div key={item.k} className="checkbox-item">
              {renderCheck(!!a[item.k])} {item.l}
            </div>
          ))}
        </div>

        {/* SECTION 5: LABS & PLANS */}
        <div className="section-title">Labs & Care Plans</div>
        <table className="info-table">
          <thead>
            <tr>
              <th style={{ width: '10%' }}>#</th>
              <th>Investigation / Plan Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="label" colSpan="2">Labs Investigations</td>
            </tr>
            {labs.length > 0 ? labs.map((lab, i) => (
              <tr key={i}><td>{i + 1}</td><td>{lab.test_name}</td></tr>
            )) : <tr><td colSpan="2" className="text-center">No labs listed</td></tr>}
            <tr>
              <td className="label" colSpan="2">Nursing Plans</td>
            </tr>
            {plans.length > 0 ? plans.map((plan, i) => (
              <tr key={i}><td>{i + 1}</td><td>{plan.plan}</td></tr>
            )) : <tr><td colSpan="2" className="text-center">No care plans listed</td></tr>}
          </tbody>
        </table>

        {/* SECTION 6: VALUABLES */}
        <div className="section-title">Patient Valuables & Belongings</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Description</td><td>{a.valuables_description || "—"}</td>
              <td className="label">Qty</td><td>{a.valuables_quantity || "—"}</td>
              <td className="label">Condition</td><td>{a.valuables_condition || "—"}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', padding: '8px', border: '1px solid #000', borderTop: 'none' }}>
          <div>Clothes: <strong>{a.handed_over_clothes}</strong></div>
          <div>Wallet: <strong>{a.handed_over_wallet}</strong></div>
          <div>Jewelry: <strong>{a.handed_over_jewelry}</strong></div>
          <div>Purse: <strong>{a.handed_over_purse}</strong></div>
        </div>
        {a.handed_over_other && (
          <div style={{ border: '1px solid #000', borderTop: 'none', padding: '8px' }}>
            Other: {a.handed_over_other}
          </div>
        )}

        {/* REFERRAL */}
        <div className="section-title">Referral Info</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Is Referred?</td><td>{a.is_referred || "No"}</td>
              <td className="label">Service</td><td>{a.referral_service || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* SIGNATURES */}
        <div className="section-title">Authentication & Review</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Family Signature</td><td>{a.family_signature || " "}</td>
              <td className="label">Relationship</td><td>{a.authenticator_relationship || "—"}</td>
            </tr>
            <tr>
              <td className="label">Nursing Staff Sign</td><td>{a.staff_signature || " "}</td>
              <td className="label">Review Supervisor</td><td>{a.review_supervisor || "—"}</td>
            </tr>
            <tr>
              <td className="label">Review Name</td><td>{a.review_name || "—"}</td>
              <td className="label">Review Date</td><td>{fmtDate(a.review_date)} {a.review_time}</td>
            </tr>
            <tr>
              <td className="label">Receiving Staff</td><td>{a.receiving_staff_name || "—"}</td>
              <td className="label">Staff Signature</td><td>{a.receiving_staff_signature || " "}</td>
            </tr>
          </tbody>
        </table>
      </div>
      </PrintWrapper>
    </>
  );
}
